/**
 * Onboarding Submit Readiness — IN-2.d
 * ────────────────────────────────────
 * Pure resolver. Returns the blockers + warnings that gate
 * `submitOnboardingSession` from the wizard.
 *
 * Distinct from IN-1.c `isReadyForAssignment()`:
 *   • That resolver operates on a real `Contractor` row.
 *   • This resolver operates on an `OnboardingSession` draft and the
 *     metadata-only document ledger that lives under the synthetic
 *     `onboarding:<sessionId>` contractor id.
 *
 * IN-2.d explicitly does NOT check:
 *   • vettingStatus / probation / executionScore
 *   • dispatch eligibility
 *   • admin approval
 *
 * Soft enforcement remains the locked default — warnings never block
 * submit; only structural gaps do.
 */

import type { ComplianceDocument } from "@/data/contractor/compliance/complianceDocuments";
import { getRequiredOnboardingCompliance } from "@/data/contractor/compliance/complianceRequirements";
import { validatePayoutMethodForEntity } from "@/data/contractor/compliance/payoutMethod";
import {
  areRequiredDocumentsUploaded,
  getOnboardingDocumentsForSession,
} from "./documents";
import { hasRequiredAgreementSignature } from "./onboardingAgreements";
import type { OnboardingSession } from "./types";
import type { Contractor } from "@/data/types";

export interface OnboardingSubmitReadiness {
  ready: boolean;
  blockers: string[];
  warnings: string[];
}

export function getOnboardingSubmitReadiness(
  session: OnboardingSession,
  documents: ComplianceDocument[],
  allSessions: OnboardingSession[] = [],
): OnboardingSubmitReadiness {
  const blockers: string[] = [];
  const warnings: string[] = [];

  // Identity — minimum viable contact info.
  const name = (session.draft.name ?? "").trim();
  const contactName = (session.draft.contactName ?? "").trim();
  const contactEmail = (session.draft.contactEmail ?? "").trim();
  if (!name) blockers.push("identity: company / legal name missing");
  if (!contactName) blockers.push("identity: primary contact name missing");
  if (!contactEmail) blockers.push("identity: contact email missing");

  // Service area — required only for executor-style roles; warning otherwise.
  // Suppressed for entities that don't render a service_area step
  // (manufacturer_partner + branch/crew/helper inherit from parent).
  const childEntity =
    session.entityType === "branch" ||
    session.entityType === "crew" ||
    session.entityType === "helper";
  const skipServiceArea =
    session.entityType === "manufacturer_partner" || childEntity;
  const hasRegions = (session.draft.serviceRegions ?? []).length > 0;
  const hasZips = (session.draft.serviceZipCodes ?? []).length > 0;
  if (!skipServiceArea && !hasRegions && !hasZips) {
    warnings.push("service_area: no regions or ZIP codes provided");
  }
  // IN-2.f — child entities without a parent binding are unsubmittable.
  if (childEntity && !session.parentEntityId) {
    blockers.push("parent: missing parent entity binding");
  }

  // Documents — required categories must all be uploaded (active).
  if (!areRequiredDocumentsUploaded(documents, session, allSessions)) {
    blockers.push("documents: required uploads missing");
  }
  // Warn if any uploaded doc is still pending verification (admin
  // hasn't reviewed yet). Never a blocker — submit is the trigger
  // that asks an admin to review.
  const docs = getOnboardingDocumentsForSession(documents, session);
  if (docs.some((d) => !d.verifiedAt)) {
    warnings.push("documents: pending admin verification");
  }

  // Agreement — required variant must be signed.
  if (!hasRequiredAgreementSignature(session)) {
    const reqs = getRequiredOnboardingCompliance(session, allSessions);
    blockers.push(`agreement: missing (${reqs.agreementVariant})`);
  }

  // Payout — must be present and structurally valid against bankingMode.
  const synthetic = {
    id: session.id,
    entityType: session.entityType,
    parentEntityId: session.parentEntityId ?? null,
    payoutMethod: session.payoutMethod,
  } as unknown as Contractor;
  const payoutBlockers = validatePayoutMethodForEntity(
    synthetic,
    session.payoutMethod,
  );
  for (const b of payoutBlockers) {
    // Unverified payout is a *warning* during beta (no real processor
    // exists), not a blocker — the IN-1.c writer always sets verified
    // false unless `placeholderVerified` was passed.
    if (b === "payout method not verified") {
      warnings.push("payout: pending verification");
    } else {
      blockers.push(`payout: ${b}`);
    }
  }

  return {
    ready: blockers.length === 0,
    blockers,
    warnings,
  };
}

// ── IN-2.g — Admin approval readiness ────────────────────────────

/**
 * Extends `getOnboardingSubmitReadiness` with admin-side gates.
 *
 * Approval is allowed when:
 *   • Session is `submitted`.
 *   • All structural blockers from submit readiness are clear.
 *   • All required documents have `verifiedAt` set (admin reviewed).
 *
 * Soft warnings remain warnings — they NEVER block approval. This
 * mirrors the spec: "approve requires assignment readiness ready OR
 * only soft warnings remain".
 *
 * Hard rules:
 *   • Does NOT check vettingStatus / dispatch / portal unlock.
 *   • Does NOT check real payout processor verification — the
 *     placeholder verified flag from IN-2.d is sufficient.
 */
export function getAdminApprovalReadiness(
  session: OnboardingSession,
  documents: ComplianceDocument[],
  allSessions: OnboardingSession[] = [],
): OnboardingSubmitReadiness {
  if (session.status === "approved") {
    return {
      ready: true,
      blockers: [],
      warnings: getOnboardingSubmitReadiness(session, documents, allSessions).warnings,
    };
  }

  const base = getOnboardingSubmitReadiness(session, documents, allSessions);
  const blockers = [...base.blockers];
  const warnings = base.warnings.filter(
    // Pending verification is a blocker for *admin approval* — promote it.
    (w) => w !== "documents: pending admin verification",
  );

  if (session.status !== "submitted") {
    blockers.push(`session: must be submitted (currently ${session.status})`);
  }

  // Require every onboarding-scoped doc to be verified.
  const docs = getOnboardingDocumentsForSession(documents, session);
  const unverified = docs.filter((d) => !d.verifiedAt && !d.rejectedAt);
  if (unverified.length > 0) {
    blockers.push(
      `documents: ${unverified.length} required upload(s) awaiting verification`,
    );
  }
  const rejected = docs.filter((d) => d.rejectedAt);
  if (rejected.length > 0) {
    blockers.push(
      `documents: ${rejected.length} rejected upload(s) must be re-submitted`,
    );
  }

  return {
    ready: blockers.length === 0,
    blockers,
    warnings,
  };
}
